import React, {useMemo, useState} from 'react';
import Modal from './Modal';
import {SearchIcon, DownloadIcon} from '../Icons';

interface TranscriptModalProps {
  isOpen: boolean;
  fileName: string;
  csv: string | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
  onDownload: () => void;
}

interface TranscriptWord {
  word: string;
  start: number;
}

/** `Word,Start,End` — the same shape /convertxml reads, so the header row is optional. */
function parseCsv(csv: string): TranscriptWord[] {
  return csv
    .split(/\r?\n/)
    .slice(csv.trimStart().toLowerCase().startsWith('word') ? 1 : 0)
    .reduce<TranscriptWord[]>((words, line) => {
      const [word, start] = line.split(',');
      if (word?.trim()) {
        words.push({word: word.trim(), start: Number.parseFloat(start) || 0});
      }
      return words;
    }, []);
}

function timecode(seconds: number): string {
  const total = Math.floor(seconds);
  const mins = String(Math.floor(total / 60)).padStart(2, '0');
  const secs = String(total % 60).padStart(2, '0');
  return `${mins}:${secs}`;
}

/**
 * Read-only look at what was actually transcribed.
 *
 * Before this, checking whether a word the user wanted censored had even been heard meant
 * downloading the CSV and opening a spreadsheet. The words are laid out as running text with a
 * timecode every so often, because that is how someone verifies a transcript — reading it — rather
 * than as a table of one word per row.
 */
const TranscriptModal: React.FC<TranscriptModalProps> = ({
  isOpen,
  fileName,
  csv,
  isLoading,
  error,
  onClose,
  onDownload,
}) => {
  const [search, setSearch] = useState('');

  const words = useMemo(() => (csv ? parseCsv(csv) : []), [csv]);
  const query = search.trim().toLowerCase();

  return (
    <Modal
      isOpen={isOpen}
      title="Transcript"
      subtitle={fileName}
      onClose={onClose}
      widthClass="max-w-2xl"
      footer={
        <button
          type="button"
          onClick={onDownload}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5
           text-sm text-gray-200 hover:bg-gray-800 transition-colors cursor-pointer"
        >
          <DownloadIcon className="w-4 h-4" />
          Download CSV
        </button>
      }
    >
      {isLoading && <p className="py-8 text-center text-sm text-gray-500">Loading the transcript…</p>}

      {error && !isLoading && (
        <p className="rounded-lg border border-red-500/30 bg-red-500/[0.06] px-3 py-2.5 text-sm text-red-200">
          {error}
        </p>
      )}

      {!isLoading && !error && csv !== null && (
        <>
          <div className="relative mb-3">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Highlight a word…"
              aria-label="Highlight a word in the transcript"
              className="w-full rounded-lg border border-gray-800 bg-gray-950/60 py-2 pl-8 pr-3 text-sm
               text-gray-100 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
               focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <p className="mb-2 text-xs text-gray-600">
            {words.length.toLocaleString()} words
            {query && ` · ${words.filter(w => w.word.toLowerCase().includes(query)).length} matching`}
          </p>

          <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-gray-800 bg-gray-950/40
           p-3 text-sm leading-7 text-gray-300">
            {words.length === 0 && <p className="text-gray-500">This transcript is empty.</p>}
            {words.map((entry, index) => (
              <React.Fragment key={`${entry.start}-${index}`}>
                {index % 60 === 0 && (
                  <span className="mr-1.5 select-none font-mono text-xs text-gray-600">
                    {timecode(entry.start)}
                  </span>
                )}
                <span
                  title={timecode(entry.start)}
                  className={
                    query && entry.word.toLowerCase().includes(query)
                      ? 'rounded bg-indigo-500/25 px-0.5 text-indigo-100'
                      : undefined
                  }
                >
                  {entry.word}
                </span>{' '}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
};

export default TranscriptModal;
